import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {PageTitleLayoutComponent} from "../../../components/page-title-layout/page-title-layout.component";
import {LotteryListComponent, LotteryListItemDirective} from "../../../components/lottery-list/lottery-list.component";
import {ApartmentService} from "../../../services/apartment.service";
import {LotteryListItemComponent} from "../../../components/lottery-list-item/lottery-list-item.component";
import {ApartmentAddressPipe} from "../../../pipes/apartment-address.pipe";
import {DatePipe} from "@angular/common";
import {LotteriesAndFooterComponent} from "../../../components/lotteries-and-footer/lotteries-and-footer.component";
import {SelectButton} from "primeng/selectbutton";
import {FormsModule} from "@angular/forms";
import {Borough, boroughs} from "../../../model/borough";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {boroughToText, BoroughToTextPipe} from "../../../pipes/borough-to-text.pipe";
import {Divider} from "primeng/divider";
import {whiteDividerDt} from "../../../theme";
import { TranslocoPipe, TranslocoService } from "@jsverse/transloco";

@Component({
  selector: 'homey-all-apartments-page',
  imports: [
    PageTitleLayoutComponent,
    LotteryListComponent,
    LotteryListItemComponent,
    LotteryListItemDirective,
    ApartmentAddressPipe,
    DatePipe,
    LotteriesAndFooterComponent,
    SelectButton,
    FormsModule,
    Divider,
    BoroughToTextPipe,
    RouterLink,
    TranslocoPipe
  ],
  templateUrl: './all-apartments-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AllApartmentsPageComponent {
  protected readonly borough = input<string | undefined>();
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly apartments = inject(ApartmentService).apartments;
  private readonly t = inject(TranslocoService);

  protected readonly parserBorough = computed<Borough | 'all'>(
    () => (this.borough() as Borough) ?? 'all'
  );

  protected readonly location = computed(
    () => {
      const b = this.parserBorough();
      // If you ever add per-borough translations, replace boroughToText with a lookup.
      return b === 'all'
        ? this.t.translate('common.borough.nyc')
        : boroughToText(b);
    }
  );

  protected readonly postFix = computed(
    () => {
      const b = this.parserBorough();
      return b === 'all'
        ? this.t.translate('common.location.postfix.inNYC')
        : this.t.translate('common.location.postfix.in', { place: boroughToText(b) });
    }
  );

  protected readonly baseRoute = computed(
    () => (this.parserBorough() === 'all' ? '.' : '../..')
  );

  protected readonly lotteries = computed(() =>
    this.apartments()?.filter(
      lottery => this.parserBorough() === 'all' || lottery.borough === this.parserBorough()
    )
  );

  protected readonly openLotteries = computed(
    () => this.lotteries()
      ?.filter(lottery => lottery.status === 'open')
      .sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime())
  );

  protected readonly boroughOptions = [{
    value: 'all',
    label: this.t.translate('common.filter.all')
  }, ...boroughs.map(
    borough => ({
      value: borough,
      label: boroughToText(borough)
    })
  )];

  protected readonly closedLotteries = computed(
    () => this.lotteries()
      ?.filter(lottery => lottery.status === 'closed')
      .sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime())
  );

  public onBoroughChange(value: string) {
    if (this.parserBorough() === 'all') {
      this.router.navigate(['boroughs', value], {
        relativeTo: this.route,
        replaceUrl: true,
      });
    } else {
      if (value === 'all') {
        this.router.navigate(['../..'], {
          relativeTo: this.route,
          replaceUrl: true,
        });
      } else {
        this.router.navigate(['..', value], {
          relativeTo: this.route,
          replaceUrl: true,
        });
      }
    }
  }

  protected readonly whiteDividerDt = whiteDividerDt;
}
